import { getApiClient } from '@/api/client';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useQuery } from '@tanstack/react-query';

const DEFAULT_DEBOUNCE_MS = 300;

export const useFilteredPriceUnits = (
  searchValue: string,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  matrixOnly?: boolean,
  countryOnly?: boolean,
) => {
  const debouncedSearch = useDebouncedValue(searchValue, debounceMs);
  return useQuery({
    queryKey: ['priceUnitsSearch', debouncedSearch, matrixOnly, countryOnly],
    queryFn: async () => {
      const response = await getApiClient().priceUnits({
        searchField: debouncedSearch,
        matrixOnly,
        countryOnly,
      });
      return response;
    },
    enabled: !!debouncedSearch,
    retry: false,
  });
};

export const usePriceUnitsPasting = (searchValue: number[]) => {
  return useQuery({
    queryKey: ['priceUnitsPaste', searchValue],
    queryFn: async () => {
      const response = await getApiClient().priceUnitsSearchMultiple({
        searchIds: searchValue,
      });
      return response;
    },
    enabled: searchValue.length > 0,
    retry: false,
  });
};

export const useFilteredProducts = (
  searchValue: string,
  debounceMs = DEFAULT_DEBOUNCE_MS,
) => {
  const debouncedSearch = useDebouncedValue(searchValue, debounceMs);
  return useQuery({
    queryKey: ['productSearch', debouncedSearch],
    queryFn: async () => {
      const response = await getApiClient().products({
        searchField: debouncedSearch,
      });
      return response;
    },
    enabled: !!debouncedSearch,
    retry: false,
  });
};

export const useProductsPasting = (searchValue: number[]) => {
  return useQuery({
    queryKey: ['productPaste', searchValue],
    queryFn: async () => {
      const response = await getApiClient().productsSearchMultiple({
        searchIds: searchValue,
      });
      return response;
    },
    enabled: searchValue.length > 0,
    retry: false,
  });
};

export const useFilteredThemes = (
  searchValue: string,
  debounceMs = DEFAULT_DEBOUNCE_MS,
) => {
  const debouncedSearch = useDebouncedValue(searchValue, debounceMs);
  return useQuery({
    queryKey: ['themes', debouncedSearch],
    queryFn: async () => {
      const response = await getApiClient().themes({
        searchField: debouncedSearch,
      });
      return response;
    },
    enabled: !!debouncedSearch,
    retry: false,
  });
};

export const useFilteredGroups = (
  searchValue: string,
  debounceMs = DEFAULT_DEBOUNCE_MS,
) => {
  const debouncedSearch = useDebouncedValue(searchValue, debounceMs);
  return useQuery({
    queryKey: ['groups', debouncedSearch],
    queryFn: async () => {
      const response = await getApiClient().groups({
        searchField: debouncedSearch,
      });
      return response;
    },
    enabled: !!debouncedSearch,
    retry: false,
  });
};

export const usePriceUnitCategories = () => {
  return useQuery({
    queryKey: ['priceUnitCategories'],
    queryFn: async () => {
      const response = await getApiClient().priceUnitCategories();
      return response;
    },
    retry: false,
  });
};
